<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
      <dropdown
        v-model="filterPeriod"
        :items="periodOptions"
        item-text="label"
        item-value="value"
        :label="$t('finance_date')"
        hide-details
        class="finance-filter-field"
      />
      <dropdown
        v-model="filterType"
        :items="typeOptions"
        item-text="label"
        item-value="value"
        :label="$t('finance_type_label')"
        clearable
        hide-details
        class="finance-filter-field"
      />
      <dropdown
        v-model="filterMoneyFlow"
        :items="moneyFlowOptions"
        item-text="label"
        item-value="value"
        :label="$t('finance_money_flow')"
        clearable
        hide-details
        class="finance-filter-field"
      />
      <dropdown
        v-if="serviceOptions.length"
        v-model="filterServiceId"
        :items="serviceOptions"
        item-text="label"
        item-value="value"
        :label="$t('finance_service_label')"
        clearable
        hide-details
        class="finance-filter-field"
      />
      <v-btn text small @click="resetFilters">{{ $t("finance_reset_filters") }}</v-btn>
    </div>

    <v-row no-gutters align="center" class="mb-3" style="gap: 12px">
      <v-col cols="12" sm="6" md="4">
        <text-field
          v-model="search"
          :label="$t('finance_search_transactions')"
          kind="search"
          clearable
          hide-details
        />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <span class="text-caption grey--text text--darken-1 mr-2">
          {{ $t("finance_selected_balance") }}
        </span>
        <span
          class="text-subtitle-1 font-weight-bold"
          :style="{ color: selectedBalance >= 0 ? '#00b37e' : '#d9534f' }"
        >
          {{ selectedBalance >= 0 ? "+" : "" }}{{ formatCurrency(selectedBalance) }}
        </span>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="text-subtitle-1 d-block">
        <div class="d-flex flex-wrap align-center" style="gap: 12px 20px">
          <span>{{ $t("finance_ledger_title") }}</span>
          <v-spacer />
          <span
            v-for="meta in moneyFlowLegend"
            :key="meta.value"
            class="d-flex align-center text-caption grey--text text--darken-2"
          >
            <span
              class="mf-dot mr-1"
              :style="{ background: meta.color }"
            />
            {{ meta.label }}
          </span>
        </div>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="transactions"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_transactions_filtered')"
        item-key="id"
        class="striped-table"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template v-slot:item.type="{ item }">
          {{ describeType(item.entryType) }}
          <span v-if="rateLabel(item)" class="rate-hint">({{ rateLabel(item) }})</span>
        </template>
        <template v-slot:item.moneyFlow="{ item }">
          <span
            v-if="moneyFlowMeta(item.moneyFlow)"
            class="d-flex align-center"
          >
            <span
              class="mf-dot mr-2"
              :style="{ background: moneyFlowMeta(item.moneyFlow).color }"
            />
            {{ $t(moneyFlowMeta(item.moneyFlow).labelKey) }}
          </span>
          <span v-else>—</span>
        </template>
        <template v-slot:item.debit="{ item }">
          <span style="color: #d9820f; font-weight: 600">
            {{ item.direction === "DEBIT" ? formatAmount(item.amount) : "—" }}
          </span>
        </template>
        <template v-slot:item.credit="{ item }">
          <span style="color: #00b37e; font-weight: 600">
            {{ item.direction === "CREDIT" ? formatAmount(item.amount) : "—" }}
          </span>
        </template>
      </v-data-table>

      <div
        v-if="totalTransactions"
        class="d-flex flex-wrap align-center justify-space-between pa-3"
        style="gap: 8px"
      >
        <span class="text-caption grey--text text--darken-1">
          {{ $t("finance_total_results", { total: totalTransactions }) }}
        </span>
        <v-pagination
          v-if="lastPage > 1"
          :value="page"
          :length="lastPage"
          :total-visible="7"
          @input="load"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import { MONEY_FLOW_META, TRANSACTION_TYPE_LABEL_KEYS, toDateParam } from "./constants";

// rate_type/rate_value (backend odgovor 16.09.2026 §3.1) - popunjeno samo na
// COMMISSION/COURIER_WAGE redovima, null na ostalima i na starijim redovima
// (prije 16.09) - ne pretpostavljati da su uvijek prisutni.
const mapTransactionDto = (dto) => ({
  id: dto.id,
  entryType: dto.entry_type,
  direction: dto.direction,
  amount: Math.abs(Number(dto.amount) || 0),
  currency: dto.currency,
  moneyFlow: dto.money_flow || null,
  sourceType: dto.source_type,
  sourceId: dto.source_id,
  rateType: dto.rate_type ?? null,
  rateValue: dto.rate_value != null ? Number(dto.rate_value) : null,
  note: dto.note,
  date: dto.created_at,
});

// 1 mjesečno, 2 procenat, 3 po obroku, 4 fiksno - vidi backend odgovor §3.1.
const RATE_TYPE_SUFFIX = { 1: "/mj", 2: "%", 3: "/obrok", 4: "" };

export default {
  name: "FinanceTabTransactions",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    transactions: [],
    serviceTypes: [],
    filterPeriod: "30d",
    filterType: null,
    filterMoneyFlow: null,
    filterServiceId: null,
    search: "",
    searchTimeout: null,
    page: 1,
    lastPage: 1,
    totalTransactions: 0,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { value: "7d", label: this.$t("finance_last_7_days") },
        { value: "30d", label: this.$t("finance_last_30_days") },
        { value: "90d", label: this.$t("finance_last_90_days") },
        { value: "all", label: this.$t("finance_all") },
      ];
    },

    typeOptions() {
      return Object.keys(TRANSACTION_TYPE_LABEL_KEYS).map((value) => ({
        value,
        label: this.$t(TRANSACTION_TYPE_LABEL_KEYS[value]),
      }));
    },

    moneyFlowOptions() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    moneyFlowLegend() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        color: MONEY_FLOW_META[value].color,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    // Filter po usluzi - prikazuje se samo ako /service-types vrati nešto.
    serviceOptions() {
      return this.serviceTypes.map((st) => ({
        value: st.id,
        label: st.name || st.label || `#${st.id}`,
      }));
    },

    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_type_label"), value: "type" },
        { text: this.$t("finance_description"), value: "note" },
        { text: this.$t("finance_money_flow"), value: "moneyFlow", sortable: false },
        {
          text: `${this.$t("finance_debit")} (${this.restaurantCurrency})`,
          value: "debit",
          align: "end",
        },
        {
          text: `${this.$t("finance_credit")} (${this.restaurantCurrency})`,
          value: "credit",
          align: "end",
        },
      ];
    },

    // Saldo za trenutno prikazani skup - računa se iz već učitanih redova.
    // DEBIT umanjuje, CREDIT uvećava saldo restorana.
    selectedBalance() {
      return this.transactions.reduce((sum, tx) => {
        const signed = tx.direction === "DEBIT" ? -tx.amount : tx.amount;
        return sum + signed;
      }, 0);
    },
  },

  watch: {
    filterPeriod() {
      this.load();
    },
    filterType() {
      this.load();
    },
    filterMoneyFlow() {
      this.load();
    },
    filterServiceId() {
      this.load();
    },
    // Backend sad podržava ?search= na cijelom filtriranom skupu (ne samo
    // trenutnoj strani) - vidi backend odgovor 14.09.2026 (N2). Debounce da
    // ne šaljemo poziv na svaki taster.
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.load(), 400);
    },
  },

  created() {
    this.loadServiceTypes();
    this.load();
  },

  methods: {
    describeType(type) {
      const key = TRANSACTION_TYPE_LABEL_KEYS[type];
      return key ? this.$t(key) : (type || "").toLowerCase().replace(/_/g, " ");
    },

    moneyFlowMeta(value) {
      return value ? MONEY_FLOW_META[value] || null : null;
    },

    // "20%" / "5.00 KM/obrok" itd. - null kad backend nije poslao rate_value
    // za taj red (stariji redovi, ili redovi bez stope - vidi napomenu na
    // mapTransactionDto).
    rateLabel(item) {
      if (item.rateValue == null) return null;
      const suffix = RATE_TYPE_SUFFIX[item.rateType] ?? "";
      if (item.rateType === 2) return `${item.rateValue}${suffix}`;
      return `${this.formatAmount(item.rateValue)} ${this.restaurantCurrency}${suffix}`;
    },

    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    // Za kolone Duguje/Potražuje - valuta je već u zaglavlju kolone, ne treba
    // je ponavljati u svakom redu.
    formatAmount(value) {
      return Number(value || 0).toFixed(2);
    },

    resetFilters() {
      this.filterPeriod = "30d";
      this.filterType = null;
      this.filterMoneyFlow = null;
      this.filterServiceId = null;
      this.search = "";
    },

    async loadServiceTypes() {
      try {
        const { data } = await restaurantFinanceService.getServiceTypes();
        const list = Array.isArray(data) ? data : data?.data || [];
        this.serviceTypes = list.filter((st) => st && st.id != null);
      } catch (error) {
        // Endpoint možda još ne postoji - filter se jednostavno ne prikaže.
        this.serviceTypes = [];
      }
    },

    // Preset -> {from, to}. "all" šalje oba kao undefined = backend default.
    periodToDateRange() {
      if (this.filterPeriod === "all") return {};
      const days = { "7d": 7, "30d": 30, "90d": 90 }[this.filterPeriod];
      const to = new Date();
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      return { from: toDateParam(from), to: toDateParam(to) };
    },

    async load(page = 1) {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { from, to } = this.periodToDateRange();
        const { data } = await restaurantFinanceService.getTransactions(this.restaurantId, {
          type: this.filterType || undefined,
          money_flow: this.filterMoneyFlow || undefined,
          service_id: this.filterServiceId || undefined,
          search: this.search || undefined,
          from,
          to,
          page,
        });
        this.transactions = (data?.data || []).map(mapTransactionDto);
        this.page = data?.meta?.current_page || 1;
        this.lastPage = data?.meta?.last_page || 1;
        this.totalTransactions = data?.meta?.total ?? this.transactions.length;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_transactions_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rate-hint {
  font-size: 0.78rem;
  color: #9aa4b2;
}

.mf-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}

.finance-filter-field {
  flex: 1 1 200px;
}

@media (min-width: 1264px) {
  .finance-filter-field {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
